import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeArSessionModal,
  openArSession,
  stopArSession
} from '../../actions/arSession';
import { getIsUserOwner } from '../../selectors/users';

/**
 * Adapt the store's AR session state to the props the AR button and its QR modal need.
 * The session itself lives in Redux, not here, so that every Run entry point can publish
 * to it — see `runSketch` in `actions/ide.js`.
 */
export default function useArSession() {
  const dispatch = useDispatch();
  const { isActive, sessionId, isOpen, error, busy } = useSelector(
    (state) => state.arSession
  );
  // Whether AR sliders are shown in AR. The sign-in checkbox (login only
  // exists to edit AR sliders) is offered only when this is on. Signing in is
  // still meaningful on a sketch you don't own: the session doc the phone logs
  // into is YOUR session, created under your uid.
  const showGuiInAr = useSelector(
    (state) => state.preferences.arSettings?.showGuiInAr ?? false
  );
  // Set only when the open sketch belongs to someone else (staff helping a student) — the
  // modal then explains that this is a private session, not a takeover of the owner's.
  // An ownerless project is a new, unsaved sketch: the current user is the owner-to-be.
  const foreignOwnerUsername = useSelector((state) => {
    const { owner } = state.project;
    if (!owner || getIsUserOwner(state)) return null;
    return owner.username || null;
  });

  const open = useCallback(() => dispatch(openArSession()), [dispatch]);
  const close = useCallback(() => dispatch(closeArSessionModal()), [dispatch]);
  const stop = useCallback(() => dispatch(stopArSession()), [dispatch]);

  return {
    isActive,
    sessionId,
    isOpen,
    error,
    busy,
    showGuiInAr,
    foreignOwnerUsername,
    open,
    close,
    stop
  };
}
